import { useState, createContext, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { X, Lock, Zap, CreditCard, ShoppingBag, ArrowRight } from "lucide-react";
import { API } from "../App";

const PaywallContext = createContext(null);

export const usePaywall = () => useContext(PaywallContext);

const ACTION_CONFIG = {
  upgrade: {
    icon: Zap,
    title: "Upgrade Your Plan",
    buttonText: "VIEW PLANS",
    buttonIcon: ArrowRight,
    color: "bg-crimson",
  },
  buy_credits: {
    icon: CreditCard,
    title: "AI Credits Exhausted",
    buttonText: "BUY CREDITS",
    buttonIcon: ShoppingBag,
    color: "bg-crimson",
  },
  buy_addon: {
    icon: ShoppingBag,
    title: "Credits Required",
    buttonText: "BROWSE ADD-ONS",
    buttonIcon: ArrowRight,
    color: "bg-crimson",
  },
};

export const PaywallProvider = ({ children }) => {
  const [paywall, setPaywall] = useState(null);
  const navigate = useNavigate();

  const showPaywall = useCallback((data) => {
    setPaywall(data);
    // Record hit to backend for nudge tracking
    fetch(`${API}/analytics/paywall-hit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        feature: data.reason || "",
        action: data.action || "upgrade",
        reason: data.reason || "",
        redirect: data.redirect || "",
        required_tier: data.required_tier || ""
      })
    }).catch(() => {});
  }, []);

  const dismiss = () => setPaywall(null);

  const handleAction = () => {
    if (paywall?.redirect) {
      navigate(paywall.redirect);
    }
    dismiss();
  };

  const config = paywall ? ACTION_CONFIG[paywall.action] || ACTION_CONFIG.upgrade : null;
  const Icon = config?.icon || Lock;

  return (
    <PaywallContext.Provider value={{ showPaywall }}>
      {children}
      <AnimatePresence>
        {paywall && (
          <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" onClick={dismiss}>
            <motion.div
              className="bg-[#111] border border-[#222] w-full max-w-sm overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="paywall-modal"
            >
              {/* Top accent */}
              <div className="h-1 bg-crimson w-full" />

              <div className="p-6">
                {/* Icon */}
                <div className="w-14 h-14 bg-crimson/10 border border-crimson/20 flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-7 h-7 text-crimson" />
                </div>

                {/* Title */}
                <h3 className="font-heading text-xl font-bold text-center mb-2" data-testid="paywall-title">
                  {config?.title}
                </h3>

                {/* Reason */}
                <p className="text-gray-400 text-sm text-center leading-relaxed mb-6" data-testid="paywall-reason">
                  {paywall.reason}
                </p>

                {/* Tier info if applicable */}
                {paywall.current_tier && paywall.required_tier && (
                  <div className="flex items-center justify-center gap-3 mb-6 text-xs">
                    <span className="px-3 py-1.5 bg-[#1a1a1a] border border-[#333] text-gray-400 uppercase tracking-wider">
                      {paywall.current_tier.replace(/_/g, " ")}
                    </span>
                    <ArrowRight className="w-4 h-4 text-crimson" />
                    <span className="px-3 py-1.5 bg-crimson/10 border border-crimson/30 text-crimson uppercase tracking-wider font-bold">
                      {paywall.required_tier.replace(/_/g, " ")}
                    </span>
                  </div>
                )}

                {/* Action Button */}
                <Button
                  onClick={handleAction}
                  className={`w-full ${config?.color} hover:opacity-90 text-white uppercase tracking-wider font-bold py-3`}
                  data-testid="paywall-action-btn"
                >
                  {config?.buttonIcon && <config.buttonIcon className="w-4 h-4 mr-2" />}
                  {config?.buttonText}
                </Button>

                {/* Dismiss */}
                <button
                  onClick={dismiss}
                  className="w-full text-center text-gray-500 text-xs mt-3 hover:text-gray-300 transition-colors uppercase tracking-wider"
                  data-testid="paywall-dismiss"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </PaywallContext.Provider>
  );
};
